/* eslint-disable no-undef, prefer-const */
// =============================================================================
// Keen‑Slider Web Component – v 4.7
// Correctif dots :
//   • Support des valeurs décimales pour `slides-per-view` (ex. 1.15)  
//   • Nombre de dots = ceil(slides − perView + 1)
// -----------------------------------------------------------------------------
// Exemples :
// <keen-slider-element slides-per-view="1.15" dots></keen-slider-element>
// =============================================================================

const KEEN_VERSION = '6.8.6'
const BASE = `https://cdn.jsdelivr.net/npm/keen-slider@${KEEN_VERSION}`
const KEEN_CSS = `${BASE}/keen-slider.min.css`
const KEEN_ESM = `${BASE}/keen-slider.es.js`

function autoplayPlugin (interval = 3000, pauseOnHover = true) {
  return slider => {
    let timer, hover = false
    const clear = () => clearTimeout(timer)
    const start = () => {
      clear(); if (hover && pauseOnHover) return
      timer = setTimeout(() => slider.next(), interval)
    }
    slider.on('created', () => {
      slider.container.addEventListener('mouseover', () => { hover = true; clear() })
      slider.container.addEventListener('mouseout', ()  => { hover = false; start() })
      start()
    })
    slider.on('dragStarted', clear)
    slider.on('animationEnded', start)
    slider.on('updated', start)
  }
}

customElements.define('keen-slider-element', class extends HTMLElement {
  static observedAttributes = ['options', 'media', 'gap']
  static _keenReady = null
  static _KeenSlider = null

  constructor () {
    super()
    this._slider = null
    this._mq = null; this._mqHandler = null
    this._dotsWrap = null; this._dots = null
    this._origSlides = 0; this._perView = 1; this._pages = 1
  }

  /* life‑cycle */
  async connectedCallback () { if (!this._slider && !this._mq) { await this.#loadDeps(); this.#setupMedia() } }
  disconnectedCallback ()   { this.#destroySlider(); this.#cleanupMedia() }
  attributeChangedCallback (n) {
    if ((n === 'options' || n === 'gap') && this._slider) this._slider.update(this.#opts())
    if (n === 'media') { this.#cleanupMedia(); this.#setupMedia() }
  }

  /* deps */
  async #loadDeps () {
    if (!document.querySelector(`link[href="${KEEN_CSS}"]`)) document.head.insertAdjacentHTML('beforeend', `<link rel="stylesheet" href="${KEEN_CSS}">`)
    if (!this.constructor._keenReady) {
      this.constructor._keenReady = import(KEEN_ESM).then(m => {
        this.constructor._KeenSlider = m?.default || m?.KeenSlider || m; return this.constructor._KeenSlider })
      .catch(()=>import(`${BASE}/keen-slider.min.js`).then(()=>{ this.constructor._KeenSlider = window.KeenSlider }))
    }
    await this.constructor._keenReady
  }

  /* media */
  #setupMedia () {
    const q = this.getAttribute('media')
    if (q) {
      this._mq = window.matchMedia(q)
      this._mqHandler = e => e.matches ? this.#ensureInit() : this.#destroySlider()
      this._mq.addEventListener('change', this._mqHandler)
      if (this._mq.matches) this.#ensureInit()
    } else { this.#ensureInit() }
  }
  #cleanupMedia () { if (this._mq && this._mqHandler) this._mq.removeEventListener('change', this._mqHandler); this._mq = this._mqHandler = null }

  /* slider */
  async #ensureInit () {
    if (this._slider) return
    await (this.constructor._keenReady || this.#loadDeps())
    const KS = this.constructor._KeenSlider; if (!KS) { console.error('KeenSlider not loaded'); return }
    this._origSlides = this.children.length
    this.classList.add('keen-slider'); Array.from(this.children).forEach(c=>c.classList.add('keen-slider__slide'))
    this.#init()
  }
  #destroySlider () { this._slider?.destroy(); this._slider = null; this._dotsWrap?.remove(); this._dotsWrap=this._dots=null }

  #init () {
    const KS = this.constructor._KeenSlider, opts = this.#opts()
    this._perView = opts.slides?.perView || 1
    this._pages   = Math.max(1, Math.ceil(this._origSlides - this._perView + 1))

    const userCreated = opts.created, userChanged = opts.slideChanged
    opts.created = s=>{ userCreated?.(s); if (this.hasAttribute('dots')) this.#buildDots(s) }
    opts.slideChanged = s=>{ userChanged?.(s); this.#updateDots(s.track.details.rel) }

    const plugins = []
    if (this.hasAttribute('autoplay')) {
      const v=this.getAttribute('autoplay'), ms=v&& !isNaN(+v)?+v:3000, hover=this.getAttribute('pause-on-hover')!=='false'
      plugins.push(autoplayPlugin(ms,hover)) }

    this._slider = new KS(this, opts, plugins)
  }

  #opts () {
    let o={}; try{ o=JSON.parse(this.getAttribute('options')??'{}') }catch{}
    const slidesOpt={...(o.slides||{})}
    if (this.hasAttribute('slides-per-view')) slidesOpt.perView = +this.getAttribute('slides-per-view')
    if (this.hasAttribute('gap')) slidesOpt.spacing = +this.getAttribute('gap')
    if (Object.keys(slidesOpt).length) o.slides = slidesOpt
    if (this.hasAttribute('loop')) o.loop = this.getAttribute('loop')!=='false'
    if (this.hasAttribute('mode')) o.mode = this.getAttribute('mode')
    return o
  }

  /* dots */
  #buildDots (slider) {
    this._dotsWrap?.remove(); const numeric=this.getAttribute('dots')==='numbers'
    this._dotsWrap=document.createElement('div'); this._dotsWrap.className='ks-dots'
    this._dots=Array.from({length:this._pages},(_,i)=>{ const b=document.createElement('button'); b.type='button'; b.className=numeric?'ks-dot ks-dot-num':'ks-dot'; b.ariaLabel=`Slide ${i+1}`; if(numeric)b.textContent=i+1; b.onclick=()=>slider.moveToIdx(i); this._dotsWrap.appendChild(b); return b })
    this.after(this._dotsWrap); this.#updateDots(0); 
    // this.constructor.#styles()
  }
  #updateDots (rel) { const a=Math.min(rel,this._pages-1); this._dots?.forEach((d,i)=>d.classList.toggle('active',i===a)) }

//   static #styles () {
//     if (document.getElementById('ks-dot-style')) return
//     document.head.insertAdjacentHTML('beforeend',`<style id="ks-dot-style">.ks-dots{display:flex;justify-content:center;gap:.5rem;margin-top:.75rem}.ks-dot{width:.7rem;height:.7rem;border-radius:50%;border:0;background:#bbb;cursor:pointer;transition:opacity .2s}.ks-dot.active{background:#111;opacity:1}.ks-dot:not(.active){opacity:.5}.ks-dot-num{width:auto;min-width:1.5rem;height:1.5rem;border-radius:9999px;padding:0 .5rem;font-size:.75rem;line-height:1.5rem;color:#fff;background:#bbb}.ks-dot-num.active{background:#111}</style>`)
//   }
})
